"use client";

import {
  LegalBulletList,
  LegalCard,
  LegalContactCard,
  LegalCtaBand,
  LegalDocumentShell,
  LegalGrid,
  LegalLedger,
  LegalLedgerRow,
  LegalMeta,
  LegalParagraphs,
  LegalStatGrid,
} from "@/components/marketing/legal-document/LegalDocumentShell";
import {
  CURRENT_DATE,
  DATA_TYPES,
  GOOGLE_WORKSPACE_DATA_GUARDRAILS,
  GOOGLE_WORKSPACE_DATA_TYPES,
  GOOGLE_WORKSPACE_DATA_USE_PARAGRAPHS,
  PRIVACY_RETENTION_STATS,
  PRIVACY_USE_CASES,
} from "../data";
import { PRIVACY_EMAIL } from "@/lib/contact";

const SCOPE_PARAGRAPHS = [
  `This Privacy Policy applies to ZOM's products and services, including applicable mobile and desktop applications (collectively, the "Services"), ZOM websites (collectively, the "Websites") and other interactions (e.g., customer service inquiries, user conferences, etc.) you may have with ZOM. If you do not agree with the terms, do not access or use the Services, Websites, or any other aspect of ZOM's business.`,
  `This Privacy Policy does not apply to any third-party applications or software that integrate with the Services through the ZOM platform ("Third-Party Services"), or any other third-party products, services or businesses. In addition, a separate agreement governs delivery, access, and use of the Services (the "MSA"), including the processing of any data submitted through the Services ("Service Data").`,
] as const;

const SERVICE_DATA_USAGE_PARAGRAPHS = [
  "Service Data will be used by ZOM in accordance with the applicable MSA, Customer's use of Services functionality, and as required by applicable law. ZOM is a processor of Service Data and Customer is the controller.",
  "ZOM uses Information in furtherance of our legitimate interests in operating our Services, Websites, and business.",
] as const;

const DATA_RETENTION_PARAGRAPH =
  "Uploaded documents and related customer content are retained to provide the Services unless and until a deletion request is submitted through the Settings page. After a verified request is received, ZOM will delete or anonymize the requested data subject to any legal, regulatory, or contractual retention obligations.";

const RIGHTS_PARAGRAPHS = [
  "Individuals located in certain US States such as California, Virginia, and countries including the European Economic Area, Switzerland, and the United Kingdom have certain statutory rights in relation to their personal data.",
  "Subject to any exemptions provided by law, you may have the right to request access to Information (in a portable form or otherwise), as well as to seek to update, delete or correct this Information.",
] as const;

const EXERCISE_RIGHTS_PARAGRAPH =
  "Requests to delete uploaded documents can be submitted through the Settings page in the ZOM product. Other privacy rights requests, or requests from users who cannot access the Settings page, may be sent via email at:";

const DELETION_RIGHT_PARAGRAPH =
  "Subject to applicable law, individuals have the right to request the deletion or erasure of their personal data and uploaded documents held by Zom Technologies LLC (“ZOM”), including rights provided under the General Data Protection Regulation (“GDPR”) Article 17 and applicable U.S. state privacy laws such as the California Consumer Privacy Act (“CCPA”).";

const DELETION_SUBMIT_PARAGRAPH =
  "Requests to delete personal data and uploaded documents should be submitted through the Settings page in the ZOM product. If you cannot access the Settings page, you may contact ZOM’s Data Privacy Officer at:";

const DELETION_VERIFICATION_PARAGRAPH =
  "To protect user privacy and security, ZOM may require verification of identity prior to processing any deletion request.";

const DELETION_PROCESS_INTRO =
  "Upon receipt and verification of a valid deletion request submitted through Settings or to the Privacy Officer, ZOM will:";

const DELETION_PROCESS_ITEMS = [
  "Review the request for applicability and legal or contractual retention obligations",
  "Identify all systems where the requester’s personal data and uploaded documents are stored, including application databases, account records, and integrated services",
  "Delete or anonymize personal data that is not subject to lawful retention requirements",
  "Ensure deletion actions are logged internally for compliance and audit purposes",
] as const;

const DELETION_RESTRICTION_PARAGRAPH =
  "Where personal data cannot be deleted due to legal, regulatory, or contractual obligations, such data will be restricted from further processing and retained only as required by law.";

const RESPONSE_SLA_PARAGRAPH =
  "ZOM will respond to verified data deletion requests within 30 days, or such shorter period as required by applicable law. If additional time is required due to the complexity of the request, ZOM will notify the requester accordingly.";

const CONFIRMATION_PARAGRAPH =
  "Once a deletion request has been completed, ZOM will provide written confirmation to the requester.";

const CTA_PARAGRAPH =
  "Please feel free to contact ZOM if you have any questions about this Privacy Policy or ZOM's practices, or if you are seeking to exercise any of your statutory rights.";

function PrivacyDataGrid() {
  return (
    <LegalGrid columns={3}>
      {DATA_TYPES.map((type, index) => (
        <LegalCard
          key={type.title}
          number={String(index + 1).padStart(2, "0")}
          meta={type.tag}
          title={type.title}
        >
          <p>{type.description}</p>
        </LegalCard>
      ))}
    </LegalGrid>
  );
}

export default function PrivacyPage() {
  return (
    <LegalDocumentShell
      eyebrow="Privacy & Data Protection"
      title="Privacy Policy, and your choices."
      description={
        <>
          This Privacy Policy describes how Zom Technologies LLC
          (&ldquo;ZOM&rdquo;) collects, uses, and discloses information, and
          what choices you have with respect to the information.
        </>
      }
      meta={<LegalMeta>Effective date · {CURRENT_DATE}</LegalMeta>}
      sections={[
        {
          id: "scope",
          eyebrow: "01 · Scope",
          title: "The scope of this policy.",
          description:
            "This Privacy Policy governs ZOM's Services, Websites, and the other interactions you have with us. A separate Master Subscription Agreement governs Service Data.",
          children: <LegalParagraphs paragraphs={SCOPE_PARAGRAPHS} />,
        },
        {
          id: "data-collection",
          eyebrow: "02 · Data collection",
          title: "Information ZOM collects and receives.",
          description:
            "ZOM may collect, generate, and receive Service Data and other information in a variety of ways depending on how you interact with our Services.",
          children: <PrivacyDataGrid />,
        },
        {
          id: "data-usage",
          eyebrow: "03 · Data usage",
          title: "How ZOM uses information.",
          description:
            "Information is used to operate and improve the Services, in line with the applicable MSA and as required by law.",
          children: (
            <LegalLedger>
              <LegalLedgerRow number="01" title="Service Data Usage">
                <LegalParagraphs paragraphs={SERVICE_DATA_USAGE_PARAGRAPHS} />
              </LegalLedgerRow>
              <div id="google-workspace-data-use" className="scroll-mt-28">
                <LegalLedgerRow number="02" title="Google Workspace Data Use">
                  <LegalParagraphs
                    paragraphs={GOOGLE_WORKSPACE_DATA_USE_PARAGRAPHS}
                  />
                  <LegalCard title="Authorized Google sources">
                    <LegalBulletList items={GOOGLE_WORKSPACE_DATA_TYPES} />
                  </LegalCard>
                  <LegalCard title="Customer controls and AI use">
                    <LegalBulletList items={GOOGLE_WORKSPACE_DATA_GUARDRAILS} />
                  </LegalCard>
                </LegalLedgerRow>
              </div>
              <LegalLedgerRow number="03" title="Specific Use Cases">
                <LegalBulletList items={PRIVACY_USE_CASES} />
              </LegalLedgerRow>
              <LegalLedgerRow number="04" title="Data Retention">
                <p>{DATA_RETENTION_PARAGRAPH}</p>
                <LegalStatGrid stats={PRIVACY_RETENTION_STATS} />
              </LegalLedgerRow>
            </LegalLedger>
          ),
        },
        {
          id: "your-rights",
          eyebrow: "04 · Your rights",
          title: "Control over your data.",
          description:
            "Depending on where you live, you may have statutory rights to access, correct, or delete your personal data.",
          children: (
            <LegalLedger>
              <LegalLedgerRow number="01" title="Statutory Rights">
                <LegalParagraphs paragraphs={RIGHTS_PARAGRAPHS} />
              </LegalLedgerRow>
              <LegalLedgerRow number="02" title="Exercise Your Rights">
                <p>{EXERCISE_RIGHTS_PARAGRAPH}</p>
                <LegalContactCard />
              </LegalLedgerRow>
            </LegalLedger>
          ),
        },
        {
          id: "data-deletion",
          eyebrow: "05 · Data deletion",
          title: "Deletion and right to erasure.",
          description:
            "You can request deletion of your personal data and uploaded documents at any time, subject to verification and lawful retention obligations.",
          children: (
            <LegalLedger>
              <LegalLedgerRow
                number="01"
                title="Your Right to Request Deletion"
              >
                <p>{DELETION_RIGHT_PARAGRAPH}</p>
              </LegalLedgerRow>
              <LegalLedgerRow number="02" title="How to Submit a Request">
                <p>{DELETION_SUBMIT_PARAGRAPH}</p>
                <LegalContactCard />
                <p>{DELETION_VERIFICATION_PARAGRAPH}</p>
              </LegalLedgerRow>
              <LegalLedgerRow number="03" title="Deletion Process">
                <p>{DELETION_PROCESS_INTRO}</p>
                <LegalBulletList items={DELETION_PROCESS_ITEMS} />
                <p>{DELETION_RESTRICTION_PARAGRAPH}</p>
              </LegalLedgerRow>
              <LegalLedgerRow number="04" title="Response Time (SLA)">
                <p>{RESPONSE_SLA_PARAGRAPH}</p>
              </LegalLedgerRow>
              <LegalLedgerRow number="05" title="Confirmation">
                <p>{CONFIRMATION_PARAGRAPH}</p>
              </LegalLedgerRow>
            </LegalLedger>
          ),
        },
      ]}
      cta={
        <LegalCtaBand
          eyebrow="Questions about this policy?"
          title="Talk to our privacy team."
          description={CTA_PARAGRAPH}
          primary="Contact Privacy Team"
          primaryHref={`mailto:${PRIVACY_EMAIL}`}
          secondary="Security page"
          secondaryHref="/security"
        />
      }
    />
  );
}
